import { getIconAsIcon } from './getIcon'
import { sortAscending } from './sortAscending'

import {
  CHECKLIST_ITEMS,
  FAVOURITE_MODULES,
  NAVIGATION_CONFIG,
} from '@/config/constants/navigation'
import { SETTINGS_CONFIG } from '@/config/constants/settings'
import {
  FilterObjectType,
  updateFiltersInLocalStorage,
} from '@/context/filterContext'
import { Role } from '@/modules/adminUsers/roles'

import {
  BuildPassModule,
  LoggedInUserRegistersUiQuery,
  LoggedInUserSettingsUiQuery,
  LoggedInUserUiQuery,
  Permission,
  ProjectSchedulesForNavUiQuery,
} from 'types/api.graphql'
import { QuerySupport, NavItemBase, NavGroupBase } from 'types/navigation'

export type NavItem = {
  id: string
  title: string
  formerlyKnownAs?: string
  querySupport?: QuerySupport
  path?: string
  icon?: NavItemBase['icon']
  iconColour?: NavItemBase['iconColour']
  iconRotate?: NavItemBase['iconRotate']
  subItems?: NavItem[]
  items?: NavItem[]
  extra?: React.ReactNode
  help?: string
  disabled?: boolean
}

export type MappedNavItem = Omit<NavItem, 'querySupport'> & {
  querySupport?:
    | QuerySupport
    | { [key: string]: QuerySupport; default: QuerySupport }
}

export type NavGroup = {
  id: string
  title: string
  items: NavItem[]
}

export const paramify = (params: object) => encodeURI(JSON.stringify(params))

export const updateFiltersIfPresent = (
  pathname: string,
  options?: FilterObjectType
) => {
  if (options) {
    updateFiltersInLocalStorage(pathname, options)
  }
  return pathname
}

/**
 * Separates the path into chunks based on the `/` character, then finds the best match
 * based on which nav item has the most matching chunks from left to right (order specific)
 * The previous logic did a simple `startsWith` check, but this caused issues when two routes
 * started with the same base path, but had different sub paths, it would always pick the first.
 * @param path the current path to match on
 * @param navItems the nav items to match against
 * @returns the best matching nav item or `null`
 */
export const findBestNavigationMatch = (
  path: string,
  navItems: MappedNavItem[]
) => {
  let bestMatch: MappedNavItem = null
  let bestMatchChunkCount = 0

  const chunkUrl = (url: string) => {
    if (!url) return []

    const split = url
      .split('?')[0] // remove query params
      .split('/') // chunk on '/'
      .filter((c) => !!c.length) // remove empty chunks

    if (!url.includes('?')) return split

    return [...split, url.split('?')[1]]
  }

  const chunks = chunkUrl(path)

  if (!chunks.length) return null

  const navItemLength = navItems?.length ?? 0

  for (let i = 0; i < navItemLength; i++) {
    const item = navItems[i]

    const itemChunks = chunkUrl(item.path)

    let matchCount = 0
    for (let j = 0; j < itemChunks.length; j++) {
      if (chunks[j] === itemChunks[j]) {
        matchCount += 1
      } else {
        matchCount = 0
        break // stop as soon as we find a mismatch
      }
    }

    // skip if no matches
    if (!matchCount) continue

    // perfect match, stop looking
    if (matchCount === chunks.length) {
      bestMatch = item
      break
    }

    if (matchCount > bestMatchChunkCount) {
      bestMatchChunkCount = matchCount
      bestMatch = item
    }
  }

  return bestMatch
}

const getItemCount = (itemCount: number) => {
  if (!itemCount) return 0

  return itemCount > 99 ? '99+' : itemCount
}

type NavigationConfigSettings = {
  userIsLimitedPermissions: boolean
  userEnabledModules: BuildPassModule[]
  userRoles: Role[]
  userPermittedModules: Pick<Permission, 'resource' | 'action'>[]
  translate: (key: string) => string
}

export class NavigationConfigBase {
  settings: NavigationConfigSettings

  constructor(settings: NavigationConfigSettings) {
    this.settings = settings
  }

  isAuthorised = (item: NavItemBase) => {
    if (!item.requiredRoles) return true

    return (
      item.requiredRoles.some((r) => this.settings.userRoles.includes(r)) ||
      this.settings.userPermittedModules?.some((p) => p.resource === item.id)
    )
  }

  userIsNotLimited = (item: NavItemBase) => {
    return !(
      item.disabledForLimitedPermissions &&
      this.settings.userIsLimitedPermissions
    )
  }

  filterNavGroup = (item: NavItemBase) => {
    return this.isAuthorised(item) && this.userIsNotLimited(item)
  }

  mapNavGroup = (group: NavGroupBase): NavGroup => ({
    id: group.id,
    title: group.title,
    items: group.items.filter(this.filterNavItems).map(this.mapNavItem),
  })

  filterNavItems = (item: NavItemBase) => {
    const isModuleEnabled =
      !item.buildPassModule ||
      this.settings.userEnabledModules.includes(
        item.buildPassModule as BuildPassModule
      )

    return (
      isModuleEnabled &&
      this.isAuthorised(item) &&
      this.userIsNotLimited(item) &&
      !item.actionsOnly
    )
  }

  mapNavItem = (item: NavItemBase) => {
    return {
      id: item.id,
      title: item.title,
      formerlyKnownAs: item.formerlyKnownAs,
      querySupport: item.querySupport,
      path: item.path,
      icon: item.icon,
      iconColour: item.iconColour,
      iconRotate: item.iconRotate,
      extra: item.extra,
      help: item.help,
      ...(item.items?.length > 0
        ? {
            subItems: item.items
              .filter(this.filterNavItems)
              .map(this.mapNavItem),
          }
        : {}),
    } as MappedNavItem
  }
}

export class NavigationConfig extends NavigationConfigBase {
  // Base level configuration
  navigationConfig: NavGroupBase[]

  // User data to splice into the navigation config
  registers: LoggedInUserRegistersUiQuery['registers']
  dashboards: LoggedInUserUiQuery['user']['adminUser']['dashboards']
  checklistCategories?: (LoggedInUserSettingsUiQuery['checklistSettings']['checklistCategories'][number] & {
    isInNavigation: boolean
  })[]

  constructor(settings: NavigationConfigSettings) {
    super(settings)

    this.navigationConfig = [FAVOURITE_MODULES, ...NAVIGATION_CONFIG]
  }

  removeDisabledCompanyInduction = (disabledCompanyInduction: boolean) => {
    if (!disabledCompanyInduction) return this

    const safetyModule = this.navigationConfig.find(
      (n) => n.id === 'safety'
    ) as NavItemBase

    if (!safetyModule?.items?.length) return this

    const inductionsModule = safetyModule.items.find(
      (n) => n.id === 'inductions'
    ) as NavItemBase

    if (!inductionsModule?.items?.length) return this

    const companyInductionIndex = inductionsModule.items.findIndex(
      (n) => n.id === 'companyInductions'
    )

    if (companyInductionIndex === -1) return this

    inductionsModule.items.splice(companyInductionIndex, 1)

    return this
  }

  addRegisters(registers: LoggedInUserRegistersUiQuery['registers']) {
    if (!registers?.length) {
      return this
    }

    const modulesConfig = this.navigationConfig.find(
      (n) => n.id === 'assetManagement'
    ) as NavItemBase

    if (!modulesConfig) return this

    const registerConfig = modulesConfig.items.find(
      (n) => n.id === 'registers'
    ) as NavItemBase

    if (registerConfig) {
      registerConfig.items = [
        {
          id: `${BuildPassModule.Registers}-ALL`,
          title: 'All Registers',
          path: '/registers#all', // prevent the menu highlight from always choosing this one
          querySupport: QuerySupport.BUILDER_ONLY,
        },
        ...registers.map((r) => ({
          id: `${BuildPassModule.Registers}-${r.identifierPrefix}`,
          title: r.name,
          path: `/registers/${r.identifierPrefix}`,
          querySupport: QuerySupport.BUILDER_ONLY,
          extra: r.itemCount ? `(${getItemCount(r.itemCount)})` : undefined,
        })),
      ]
    }

    return this
  }

  findItemByIdAndBuildLabel(
    items: NavItemBase[],
    id: string,
    parentLabel = ''
  ): NavItemBase | null {
    for (const item of items) {
      const title = this.settings.translate(item.title)

      const currentLabel = parentLabel ? `${parentLabel} | ${title}` : title

      if (item.id === id) {
        return { ...item, title: currentLabel, icon: null }
      }
      if (item.items && item.items.length > 0) {
        const found = this.findItemByIdAndBuildLabel(
          item.items,
          id,
          currentLabel
        )
        if (found) {
          return found
        }
      }
    }
    return null
  }

  addFavourites(
    favourites: LoggedInUserSettingsUiQuery['userSettings']['favourites']
  ) {
    const favouritesConfig = this.navigationConfig.find(
      (n) => n.id === 'favourites'
    ) as NavItemBase

    if (!favouritesConfig) {
      return this
    }

    favouritesConfig.items =
      favourites && favourites.length
        ? favourites
            .map((favId) => {
              const item = this.findItemByIdAndBuildLabel(
                this.navigationConfig.flatMap((g) => g.items),
                favId
              )
              return item ? { ...item, title: item.title } : null
            })
            .filter(Boolean)
        : []

    return this
  }

  addDashboards(
    dashboards: LoggedInUserUiQuery['user']['adminUser']['dashboards'],
    selectedProjectId?: string
  ) {
    const dashboardsListConfig = this.navigationConfig.find(
      (n) => n.id === 'dashboards'
    ) as NavItemBase

    if (!dashboardsListConfig) return this

    const dashboardConfig = dashboardsListConfig.items.find(
      (n) => n.id === 'myDashboards'
    ) as NavItemBase

    if (!dashboardConfig) {
      return this
    }

    if (!dashboards?.length) {
      dashboardConfig.items = undefined
    } else {
      dashboards = [...dashboards].filter((d) =>
        !selectedProjectId
          ? true
          : d.projects?.length === 0 ||
            d.projects?.some((p) => p.id === selectedProjectId)
      )

      dashboardConfig.items = dashboards?.length
        ? [
            {
              id: `${BuildPassModule.MyDashboards}`,
              title: 'All dashboards',
              path: '/dashboards',
              querySupport: QuerySupport.BUILDER_AND_PROJECT,
            },
            ...dashboards
              .sort((p1, p2) => {
                return p1.title
                  .toLowerCase()
                  .localeCompare(p2.title.toLowerCase())
              })
              .map((dashboard) => ({
                id: dashboard.id,
                title: dashboard.title,
                path: `/dashboards/${dashboard.id}`,
                querySupport: QuerySupport.BUILDER_AND_PROJECT,
              })),
          ]
        : undefined
    }
    return this
  }

  addChecklistCategories(
    canAccessChecklistCategories: boolean,
    checklistCategories: (LoggedInUserSettingsUiQuery['checklistSettings']['checklistCategories'][number] & {
      isInNavigation: boolean
    })[]
  ) {
    const siteManagementNavConfig = this.navigationConfig.find(
      (n) => n.id === 'siteManagement'
    ) as NavItemBase

    if (!siteManagementNavConfig) return this

    const checklistsNavConfig = siteManagementNavConfig.items.find(
      (n) => n.id === 'checklists'
    ) as NavItemBase

    if (!checklistsNavConfig) {
      return this
    }

    const checklistCategoriesInNavigation = sortAscending(
      checklistCategories.filter((c) => c.isInNavigation),
      'label'
    )

    if (
      !checklistCategoriesInNavigation?.length ||
      !canAccessChecklistCategories
    ) {
      checklistsNavConfig.items = CHECKLIST_ITEMS
    } else {
      checklistsNavConfig.items = [
        {
          ...CHECKLIST_ITEMS?.[0],
          path: `/checklists/category/all`,
          title: 'All forms',
        },
        ...checklistCategoriesInNavigation.map((category) => ({
          id: category.id,
          title: category.label,
          ...(category.icon
            ? {
                icon: getIconAsIcon(category.icon),
                iconColour: category.colour,
                noIcon: false,
              }
            : {
                noIcon: true,
              }),
          path: `/checklists/category/${category.id}`,
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
        })),
        ...(CHECKLIST_ITEMS?.slice(1) || []),
      ]
    }
    return this
  }

  configureSchedulingModule = (
    projectSchedulesData: ProjectSchedulesForNavUiQuery
  ) => {
    if (!projectSchedulesData) {
      return this // haven't fetched yet
    }

    const projModule = this.navigationConfig.find(
      (c) => c.id === 'projectManagement'
    )

    const schedulingModule = projModule?.items.find(
      (i) => i.id === 'scheduling'
    )

    if (!schedulingModule) {
      return this
    }

    const ids = projectSchedulesData?.projectSchedules_admin?.map((s) => s.id)

    if (!ids?.length) {
      schedulingModule.path = '/scheduling/new' // if no schedules, redirect to new
    } else if (ids.length === 1) {
      schedulingModule.path = schedulingModule.actions.view(ids[0]) // if only one schedule, redirect to it
    } else {
      schedulingModule.path = '/scheduling' // if multiple schedules, redirect to list
    }

    return this
  }

  hasContent = (items: NavItem[]): boolean => {
    if (!items?.length) return false

    return items.some((item) => {
      // If the item has a path, it's a leaf node with content
      if (item.path) return true

      // If it has subItems, check if any of them have content
      if (item.subItems?.length) {
        return this.hasContent(item.subItems)
      }

      return false
    })
  }

  get() {
    return this.navigationConfig
      .filter(this.filterNavGroup)
      .map(this.mapNavGroup)
      .filter((group: NavGroup) => this.hasContent(group.items))
  }
}

export class SettingsConfig extends NavigationConfigBase {
  settingsConfig: NavItem[]

  constructor(settings: NavigationConfigSettings) {
    super(settings)

    this.settingsConfig = SETTINGS_CONFIG
  }

  get() {
    return this.settingsConfig.filter(this.filterNavGroup).map(this.mapNavItem)
  }
}
